import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router';
import React from 'react';
import { twMerge } from 'tailwind-merge';
import TablerIconHome from '~icons/tabler/home';

import { Slide, Stack, Typography } from 'gooey';
import { GlowCard } from '#/components/design';
import { ButtonLink } from '#/components/links';
import { CustomerLogoList, CustomerTestimonialCard } from '#/components/widgets';
import fx from '#/fx';
import { ClientOnly } from '#/lib/client-only';
import { pageSEO } from '#/lib/content';
import { CalendlyBookingWidget, useCalendlyEventListener } from '#/vendors/calendly';

export const Route = createFileRoute('/_blank/booking/$id')({
  loader: async ({ params }) => {
    const page = await load(params.id);
    return page;
  },
  head: ({ loaderData, match }) => ({
    meta: pageSEO(
      {
        title: 'Book a meeting with Joggr',
        description: 'Book a meeting with Joggr to learn more about our products and services.',
        keywords: ['book', 'meeting', 'calendly'],
      },
      match,
      loaderData
    ),
  }),
  component: BookingPage,
});

async function load(id: string) {
  const page = await fx.getPageContentBySlug({
    data: {
      key: 'booking',
      slug: id,
    },
  });

  const testimonials = await fx.listCustomerTestimonials({
    data: {
      fields: {
        ids: [{ id: page.sections.sidebar.featuredTestimonialId }],
      },
    },
  });
  const customerLogos = await fx.listCustomerLogos({
    data: {
      fields: {
        ids: page.sections.sidebar.logos.map((id, i) => ({ id, order: i })),
      },
    },
  });
  return {
    ...page,
    sections: {
      ...page.sections,
      sidebar: {
        ...page.sections.sidebar,
        featuredTestimonial: testimonials[0],
        customerLogos: customerLogos.map((logo) => logo),
      },
    },
  };
}

type LoaderData = Awaited<ReturnType<typeof load>>;

function BookingPage() {
  const params = useParams({
    from: '/_blank/booking/$id',
  });
  const [hideBar, setHideBar] = React.useState(false);
  const page = Route.useLoaderData();
  const { sections } = page;
  const nav = useNavigate();

  useCalendlyEventListener({
    onDateAndTimeSelected() {
      setHideBar(true);
    },
    onEventTypeViewed() {
      setHideBar(false);
    },
    onEventScheduled() {
      nav({
        to: '/booking/$id/confirmation',
        params: {
          id: params.id,
        },
      });
    },
  });

  return (
    <div className='w-full h-full min-h-screen bg-background-default'>
      <ClientOnly>
        {() => (
          <div className='w-full'>
            <div
              className={twMerge(
                'sm:[&_.calendly-inline-widget]:min-w-[480px] w-full min-h-screen flex items-center justify-center [&_.calendly-inline-widget]:w-full',
                !hideBar && 'lg:w-[calc(100%-600px)]'
              )}
            >
              <PageSectionBooking sections={sections} />
            </div>
            <Slide direction='left' in={!hideBar}>
              {(transitionProps) => (
                <div
                  {...transitionProps}
                  className={twMerge(
                    'w-[600px] min-h-screen items-center justify-center flex-col px-4 fixed right-0 top-0 bottom-0 bg-background-paper',
                    transitionProps.className,
                    'hidden lg:flex'
                  )}
                >
                  <div
                    className='z-0 brightness-40 absolute inset-0 h-full w-full'
                    style={{
                      backgroundImage: 'url(/images/backgrounds/dots-white.png)',
                    }}
                  />
                  <PageSectionSidebar sections={sections} />
                </div>
              )}
            </Slide>
          </div>
        )}
      </ClientOnly>
    </div>
  );
}

function PageSectionBooking({ sections }: BookingPageSectionProps) {
  return (
    <CalendlyBookingWidget id={sections.booking.bookingId} title={sections.booking.bookingId} />
  );
}

function PageSectionSidebar({ sections }: BookingPageSectionProps) {
  return (
    <Stack className='z-10' alignItems='center' spacing={3}>
      <div className='max-w-[480px] w-full'>
        <GlowCard className='z-10' border='top'>
          <Typography variant='h6' className='text-center mb-8'>
            Trusted by the best software teams
          </Typography>
          <CustomerLogoList
            className='[&>.quark-customer-logo]:w-[45%]'
            customerLogos={sections.sidebar.customerLogos}
            opacity={0.8}
          />
        </GlowCard>
      </div>
      <div className='max-w-[480px] w-full'>
        <CustomerTestimonialCard testimonial={sections.sidebar.featuredTestimonial} />
      </div>
      <ButtonLink
        startIcon={<TablerIconHome />}
        to='/'
        variant='outlined'
        color='primary'
        className='mt-8 bg-slate-900 hover:bg-slate-800'
        size='large'
      >
        Go back to the homepage
      </ButtonLink>
    </Stack>
  );
}

type BookingPageSectionProps = Pick<LoaderData, 'sections'>;
